/**
 * The bank directory, and a logo per bank.
 *
 * The list is read-only on purpose: it is whatever the payout provider last told the
 * worker, and a bank list an operator could hand-edit would drift from what the rail will
 * actually pay. What an operator owns here is the artwork — a withdrawal picker of three
 * hundred identical grey marks is a bad screen, and only a person can say which logo
 * belongs to which bank.
 *
 * Each file is validated client-side (an allowed raster mime, ≤ 200 KB) and sent as base64
 * in a PUT, exactly like the operator logo on Brand assets. The server sniffs the bytes
 * regardless — this check only saves a round trip on an obvious mistake. Uploading is
 * gated on `providers:write` (finance, superadmin), the same grant as payout routing.
 *
 * `code` is shown here and nowhere a customer can see it: an operator reconciling against
 * the PSP's dashboard needs the NIBSS code, a customer never does.
 *
 * The list is **searched and windowed**, not rendered whole. Paystack's Nigerian directory
 * is around 250 institutions, each row here carrying an image, a file input and a button,
 * and the screen was written when the table held ten rows because the worker was syncing a
 * stub. Sixty rows at a time plus the search box and the "missing a logo" filter is what
 * makes it a working screen rather than a wall.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { API_ORIGIN, USING_MOCK } from '../api/config.js';
import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import { API_PREFIX } from '../contracts/routes.js';
import type { AdminBank, BrandAssetMime } from '../contracts/types.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBox,
  LoadingState,
  SearchInput,
  Select,
} from '../components/ui.js';
import { IconImage } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { relativeTime } from '../lib/format.js';

const MAX_BYTES = 200 * 1024;

/**
 * How many rows are rendered before the operator asks for more.
 *
 * The directory is not ten banks. Paystack's Nigerian list is around 250 institutions once
 * microfinance banks are counted, and every row here carries a live `<img>`, a file input
 * and a button — so the whole list at once is roughly a thousand extra nodes and 250 image
 * requests for a screen where the operator is looking for one bank. A window plus the
 * search box above it is the whole answer: nobody scrolls a 250-row table, they type.
 *
 * A growing window rather than numbered pages, deliberately. The job this screen exists for
 * is uploading logos one after another, and a page that reshuffles under you each time a
 * row changes — which is exactly what "missing a logo" filtering plus paging would do — is
 * a screen you lose your place in.
 */
const PAGE = 60;

/** What the operator is looking at. `no-logo` is the one that matches the actual job. */
type Filter = 'all' | 'no-logo' | 'listed' | 'delisted';

const FILTER_LABELS: Readonly<Record<Filter, string>> = {
  all: 'All banks',
  'no-logo': 'Missing a logo',
  listed: 'Listed by the provider',
  delisted: 'No longer listed',
};

function matchesFilter(bank: AdminBank, filter: Filter): boolean {
  switch (filter) {
    case 'no-logo':
      return bank.active && bank.logoPath === null;
    case 'listed':
      return bank.active;
    case 'delisted':
      return !bank.active;
    default:
      return true;
  }
}

/** The raster mimes the contract accepts. Mirrors `BRAND_ASSET_MIMES` — no SVG. */
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const satisfies readonly BrandAssetMime[];

function isAllowedMime(mime: string): mime is BrandAssetMime {
  return (ALLOWED_MIMES as readonly string[]).includes(mime);
}

/** Read a File as bare base64 (no `data:` prefix), for the upload body. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * The absolute URL for a stored logo.
 *
 * The API returns an API-relative *path* because only the client knows which origin and
 * prefix it is talking to, so the console composes both here. Against the in-memory demo
 * backend there are no stored bytes at all, so there is nothing to point at and the row
 * falls back to its placeholder.
 */
function logoSrc(bank: AdminBank): string | null {
  if (USING_MOCK || bank.logoPath === null) return null;
  return `${API_ORIGIN}${API_PREFIX}${bank.logoPath}`;
}

export function BanksPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'providers:write');
  const state = useAsync(() => api.listBanks(), []);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [visible, setVisible] = useState(PAGE);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Memoised, not `?? []` inline: a fresh empty array on every render makes it a new
  // dependency every render, so the filter below re-ran on each keystroke over a directory
  // of several hundred banks even when nothing had changed.
  const banks = useMemo(() => state.data?.banks ?? [], [state.data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return banks.filter(
      (b) =>
        matchesFilter(b, filter) &&
        (q === '' || b.name.toLowerCase().includes(q) || b.code.toLowerCase().includes(q)),
    );
  }, [banks, query, filter]);

  // Back to the top of the window whenever the operator changes what they are looking at.
  // Without this, narrowing 250 banks to 3 leaves a window sized for the old list and the
  // "show more" control claiming there is more to see when there is not.
  useEffect(() => setVisible(PAGE), [query, filter]);

  const shown = useMemo(() => filtered.slice(0, visible), [filtered, visible]);
  const withoutLogo = banks.filter((b) => b.active && b.logoPath === null).length;

  async function upload(bank: AdminBank, file: File | undefined) {
    if (file === undefined) return;
    if (!isAllowedMime(file.type)) {
      toast.error('Unsupported image', 'Choose a PNG, JPG or WebP logo.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File too large', 'Keep a bank logo under 200 KB.');
      return;
    }

    setBusyId(bank.id);
    try {
      const dataBase64 = await toBase64(file);
      const updated = await api.uploadBankLogo(bank.id, { mime: file.type, dataBase64 });
      state.setData((prev) =>
        prev === null ? prev : { ...prev, banks: prev.banks.map((b) => (b.id === updated.id ? updated : b)) },
      );
      toast.success('Logo uploaded', `${updated.name} now shows its own mark in the app.`);
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  if (state.loading) return <LoadingState label="Loading banks…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const lastSyncedAt = state.data?.lastSyncedAt ?? null;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Banks</h1>
        <p className="page-head__sub">
          The bank list customers pick from when withdrawing. It syncs from your payout provider automatically, so you cannot add or rename banks here. You can only add logos.
          {!canWrite && ' Your role can view the directory but not upload logos.'}
        </p>
      </div>

      <div className="grid grid--3" style={{ marginBottom: 16 }}>
        <Card title="Banks listed">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{banks.filter((b) => b.active).length}</div>
          <div className="faint" style={{ fontSize: 12 }}>
            {banks.length - banks.filter((b) => b.active).length} no longer listed by the provider
          </div>
        </Card>
        <Card title="Missing a logo">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{withoutLogo}</div>
          <div className="faint" style={{ fontSize: 12 }}>Shown with the app&rsquo;s generic bank mark</div>
        </Card>
        <Card title="Directory synced">
          <div style={{ fontSize: 26, fontWeight: 650 }}>
            {lastSyncedAt === null ? 'Never' : relativeTime(lastSyncedAt)}
          </div>
          <div className="faint" style={{ fontSize: 12 }}>The worker refreshes it daily</div>
        </Card>
      </div>

      <div className="toolbar">
        <div className="toolbar__grow">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by bank name or code…" />
        </div>
        <Select
          value={filter}
          aria-label="Filter the directory"
          onChange={(e) => setFilter(e.target.value as Filter)}
        >
          {(Object.keys(FILTER_LABELS) as Filter[]).map((key) => (
            <option key={key} value={key}>
              {FILTER_LABELS[key]}
            </option>
          ))}
        </Select>
      </div>

      <Card flush>
        {filtered.length === 0 ? (
          <EmptyState
            glyph="🏦"
            title={banks.length === 0 ? 'No banks synced yet' : 'No bank matches'}
            hint={
              banks.length === 0
                ? 'The worker populates this from the payout provider on its next run.'
                : 'Try a different name or code, or widen the filter.'
            }
          />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Logo</th>
                    <th>Bank</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Logo updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((bank) => (
                    <BankRow
                      key={bank.id}
                      bank={bank}
                      canWrite={canWrite}
                      busy={busyId === bank.id}
                      onChoose={(file) => void upload(bank, file)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {/*
              Always stated, not only when there is more to load. A count that appears and
              disappears is a count nobody trusts, and "showing 60 of 251" is the single
              most useful line on a screen whose whole failure mode was a list that looked
              complete and was not.
            */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 14px',
                borderTop: '1px solid var(--border, rgba(127,127,127,0.2))',
              }}
            >
              <span className="faint" style={{ fontSize: 12 }}>
                Showing {shown.length} of {filtered.length}
                {filtered.length !== banks.length && ` (filtered from ${banks.length})`}
              </span>
              {shown.length < filtered.length && (
                <Button size="sm" variant="ghost" onClick={() => setVisible((v) => v + PAGE)}>
                  Show {Math.min(PAGE, filtered.length - shown.length)} more
                </Button>
              )}
            </div>
          </>
        )}
      </Card>
    </>
  );
}

function BankRow({
  bank,
  canWrite,
  busy,
  onChoose,
}: {
  bank: AdminBank;
  canWrite: boolean;
  busy: boolean;
  onChoose: (file: File | undefined) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const src = logoSrc(bank);

  return (
    <tr>
      <td>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            background: 'var(--surface-2, rgba(127,127,127,0.12))',
          }}
        >
          {src === null ? (
            <IconImage size={16} />
          ) : (
            // `lazy` because a windowed table still holds sixty of these, and a logo the
            // operator has scrolled past is a request that bought nothing.
            <img
              src={src}
              alt=""
              loading="lazy"
              decoding="async"
              width={36}
              height={36}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
        </div>
      </td>
      <td style={{ fontWeight: 600 }}>{bank.name}</td>
      <td className="mono faint">{bank.code}</td>
      <td>
        {bank.active ? (
          <Badge tone="green">Listed</Badge>
        ) : (
          <Badge tone="neutral">Delisted</Badge>
        )}
      </td>
      <td className="muted">
        {bank.logoUpdatedAt === null ? 'No logo yet' : relativeTime(bank.logoUpdatedAt)}
      </td>
      <td style={{ textAlign: 'right' }}>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canWrite}
          loading={busy}
          onClick={() => inputRef.current?.click()}
        >
          {bank.logoPath === null ? 'Upload logo' : 'Replace'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          disabled={!canWrite}
          onChange={(e) => {
            onChoose(e.target.files?.[0]);
            // Clear it, or choosing the same file twice after a failure fires no change event.
            e.target.value = '';
          }}
        />
      </td>
    </tr>
  );
}
